import type { Attachment } from '@/lib/types';

/**
 * Per-agent chat-input draft persistence.
 *
 * Mirrors the strategy used by `launch-agent-dialog/persistence.ts`:
 * - localStorage for small, text-only drafts (cheap and synchronous-ish)
 * - IndexedDB for any draft that carries attachments (base64 payloads can
 *   easily blow past localStorage's ~5MB quota)
 *
 * Keyed by `agentId` so each thread keeps its own in-flight composition.
 */

const LOCAL_STORAGE_KEY_PREFIX = 'reflex:agent-thread-draft:';
const DB_NAME = 'reflex-agent-thread-draft-storage';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';
const LOCAL_STORAGE_SIZE_LIMIT = 1024 * 1024;

export interface AgentThreadDraft {
  text: string;
  attachments: Attachment[];
}

function localStorageKey(agentId: string): string {
  return `${LOCAL_STORAGE_KEY_PREFIX}${agentId}`;
}

export function saveDraft(agentId: string, draft: AgentThreadDraft): void {
  try {
    const hasAttachments = draft.attachments.length > 0;
    const serialized = JSON.stringify(draft);
    if (hasAttachments || serialized.length > LOCAL_STORAGE_SIZE_LIMIT) {
      void saveToIndexedDB(agentId, draft);
      localStorage.removeItem(localStorageKey(agentId));
    } else {
      localStorage.setItem(localStorageKey(agentId), serialized);
      void clearFromIndexedDB(agentId);
    }
  } catch (error) {
    console.warn('Failed to save agent thread draft:', error);
  }
}

export async function loadDraft(agentId: string): Promise<AgentThreadDraft | null> {
  try {
    const indexedData = await loadFromIndexedDB(agentId);
    if (indexedData) return indexedData;

    const stored = localStorage.getItem(localStorageKey(agentId));
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<AgentThreadDraft>;
    return {
      text: typeof parsed.text === 'string' ? parsed.text : '',
      attachments: Array.isArray(parsed.attachments) ? parsed.attachments : [],
    };
  } catch (error) {
    console.warn('Failed to load agent thread draft:', error);
    return null;
  }
}

export function clearDraft(agentId: string): void {
  try {
    localStorage.removeItem(localStorageKey(agentId));
    void clearFromIndexedDB(agentId);
  } catch (error) {
    console.warn('Failed to clear agent thread draft:', error);
  }
}

function saveToIndexedDB(agentId: string, draft: AgentThreadDraft): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.put(draft, agentId);

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

function loadFromIndexedDB(agentId: string): Promise<AgentThreadDraft | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.close();
        resolve(null);
        return;
      }

      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(agentId);

      getRequest.onsuccess = () => {
        db.close();
        resolve((getRequest.result as AgentThreadDraft | undefined) ?? null);
      };
      getRequest.onerror = () => {
        db.close();
        reject(getRequest.error);
      };
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

function clearFromIndexedDB(agentId: string): Promise<void> {
  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => resolve();
    request.onsuccess = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.close();
        resolve();
        return;
      }

      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.delete(agentId);

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        resolve();
      };
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}
